import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Shield, Send, CheckCircle, AlertCircle } from 'lucide-react';

export default function ApplyVerifierPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    experience: '',
    motivation: '',
    expertise: [],
    availability: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const expertiseOptions = [
    'Cryptocurrency',
    'Blockchain Analysis',
    'Cybersecurity',
    'Fraud Investigation',
    'Legal/Compliance',
    'Financial Services',
    'DeFi Protocols',
    'Smart Contracts'
  ];

  const handleExpertiseToggle = (option) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(option)
        ? prev.expertise.filter(e => e !== option)
        : [...prev.expertise, option]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (formData.experience.length < 100) {
      setError('Please provide at least 100 characters of experience');
      setLoading(false);
      return;
    }

    if (formData.motivation.length < 50) {
      setError('Please provide at least 50 characters explaining your motivation');
      setLoading(false);
      return;
    }

    if (formData.expertise.length === 0) {
      setError('Please select at least one area of expertise');
      setLoading(false);
      return;
    }

    try {
      // Check if user already applied
      const applicationsRef = collection(db, 'verifierApplications');
      const q = query(applicationsRef, where('userId', '==', currentUser.uid));
      const existingApps = await getDocs(q);

      if (!existingApps.empty) {
        setError('You have already submitted an application. Please wait for admin review.');
        setLoading(false);
        return;
      }

      // Submit application
      await addDoc(applicationsRef, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email,
        experience: formData.experience,
        motivation: formData.motivation,
        expertise: formData.expertise,
        availability: formData.availability,
        status: 'pending', // pending, approved, rejected
        createdAt: serverTimestamp(),
        reviewedAt: null,
        reviewedBy: null,
        reviewNotes: ''
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/home');
      }, 3000);
    } catch (err) {
      console.error('Error submitting application:', err);
      setError('Failed to submit application. Please try again.');
    }
    setLoading(false);
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 max-w-md text-center">
          <AlertCircle size={64} className="mx-auto mb-4 text-yellow-400" />
          <h2 className="text-2xl font-bold text-white mb-2">Login Required</h2>
          <p className="text-gray-400">Please log in to apply as a verifier.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 max-w-md text-center">
          <CheckCircle size={64} className="mx-auto mb-4 text-green-400" />
          <h2 className="text-2xl font-bold text-white mb-2">Application Submitted!</h2>
          <p className="text-gray-400 mb-4">
            Thank you for applying to become an expert verifier. Our team will review your application and get back to you soon.
          </p>
          <p className="text-sm text-gray-500">Redirecting to home...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500/20 to-blue-500/20 flex items-center justify-center">
              <Shield size={32} className="text-green-400" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Apply to Become a Verifier</h1>
          <p className="text-gray-400 text-lg">
            Join our team of expert verifiers and help protect the community from fraud
          </p>
        </div>

        {/* Application Form */}
        <div className="glass-card rounded-2xl p-8 border border-green-500/20">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Experience */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Relevant Experience *
              </label>
              <textarea
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                placeholder="Tell us about your experience with cryptocurrency, fraud detection, or related fields..."
                className="w-full glass-card rounded-lg p-4 text-white placeholder-gray-500 min-h-[150px] focus:outline-none focus:ring-2 focus:ring-green-500/50"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.experience.length} / 100 minimum characters
              </p>
            </div>

            {/* Motivation */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Why do you want to be a verifier? *
              </label>
              <textarea
                value={formData.motivation}
                onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                placeholder="Explain your motivation for joining the verification team..."
                className="w-full glass-card rounded-lg p-4 text-white placeholder-gray-500 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-green-500/50"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.motivation.length} / 50 minimum characters
              </p>
            </div>

            {/* Expertise */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Areas of Expertise * (select all that apply)
              </label>
              <div className="grid grid-cols-2 gap-3">
                {expertiseOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleExpertiseToggle(option)}
                    className={`p-3 rounded-lg border transition-all ${
                      formData.expertise.includes(option)
                        ? 'bg-green-500/20 border-green-500/40 text-green-300'
                        : 'glass-card border-gray-700 text-gray-400 hover:border-green-500/30'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Availability */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Weekly Availability
              </label>
              <select
                value={formData.availability}
                onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                className="w-full glass-card rounded-lg p-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500/50"
              >
                <option value="">Select availability</option>
                <option value="1-5">1-5 hours per week</option>
                <option value="5-10">5-10 hours per week</option>
                <option value="10-20">10-20 hours per week</option>
                <option value="20+">20+ hours per week</option>
              </select>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-500/40 rounded-lg text-green-300 font-bold hover:from-green-500/30 hover:to-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-400"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Submit Application
                </>
              )}
            </button>
          </form>

          {/* Info Box */}
          <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <h4 className="font-semibold text-blue-300 mb-2">What happens next?</h4>
            <ul className="text-sm text-gray-400 space-y-1">
              <li>• Your application will be reviewed by our admin team</li>
              <li>• We typically respond within 3-5 business days</li>
              <li>• If approved, you'll receive verifier access</li>
              <li>• You can start reviewing fraud reports immediately</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
